import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation,useNavigate, useParams } from 'react-router-dom';

import type { OrdersTableTab } from '@ekhodealer/ekho-common';

import { paths } from '@config/paths';

import { OrderDetailsTab } from '../atoms/orderDetailsDrawerAtoms';
import { currentOrdersListAtom, currentPageAtom, itemsPerPageAtom } from '../atoms/ordersAtoms';

/**
 * Custom hook for order-related navigation
 * Encapsulates the logic for navigating to order details and closing the drawer
 *
 * @example
 * ```tsx
 * const { openOrderDetails, closeOrderDetails, navigateToPreviousOrder, navigateToNextOrder } = useOrderNavigation();
 *
 * // Open order details to a specific tab
 * openOrderDetails('order-123', OrderDetailsTab.PROGRESS);
 *
 * // Navigate to adjacent orders
 * navigateToPreviousOrder('order-123');
 * navigateToNextOrder('order-123');
 *
 * // Close the drawer
 * closeOrderDetails();
 * ```
 */
export function useOrderNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { servicesProviderId, tabId, orderId } = useParams<{
		servicesProviderId: string;
		tabId: string;
		orderId?: string;
	}>();

	// Get current orders list and pagination state
	const currentOrdersList = useAtomValue(currentOrdersListAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Track pending navigation after page change
	const pendingNavigationRef = useRef<{
		action: 'next' | 'prev';
		targetTab: OrderDetailsTab;
	} | null>(null);

	/**
	 * Navigate to order details with a specific tab
	 * @param orderId - The ID of the order to open
	 * @param detailsTab - The tab to open (defaults to PROGRESS)
	 */
	const openOrderDetails = useCallback(
		(orderId: string, detailsTab: OrderDetailsTab = OrderDetailsTab.PROGRESS) => {
			if (!servicesProviderId || !tabId) {
				console.warn(
					'Cannot navigate to order details: missing servicesProviderId or tabId',
				);
				return;
			}

			// Preserve query parameters (filters, pagination, etc.) when opening details
			const path = paths.orderDetails.getHref(
				servicesProviderId,
				tabId as OrdersTableTab,
				orderId,
				detailsTab,
			);
			navigate(path + location.search);
		},
		[navigate, servicesProviderId, tabId, location.search],
	);

	/**
	 * Close the order details drawer and return to the orders list
	 */
	const closeOrderDetails = () => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot close order details: missing servicesProviderId or tabId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when closing details
		const path = paths.ordersTab.getHref(servicesProviderId, tabId as OrdersTableTab);
		navigate(path + location.search);
	};

	/**
	 * Change the active tab in the order details drawer
	 * @param orderId - The ID of the currently open order
	 * @param detailsTab - The new tab to switch to
	 */
	const switchOrderDetailsTab = (orderId: string, detailsTab: OrderDetailsTab) => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot switch tab: missing servicesProviderId or tabId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when switching tabs
		const path = paths.orderDetails.getHref(
			servicesProviderId,
			tabId as OrdersTableTab,
			orderId,
			detailsTab,
		);
		navigate(path + location.search);
	};

	/**
	 * Navigate to the previous order in the list
	 * Handles pagination automatically if needed
	 * @param currentOrderId - The ID of the currently displayed order
	 * @param detailsTab - The tab to maintain when navigating (defaults to PROGRESS)
	 */
	const navigateToPreviousOrder = (
		currentOrderId: string,
		detailsTab: OrderDetailsTab = OrderDetailsTab.PROGRESS,
	) => {
		const currentIndex = currentOrdersList.indexOf(currentOrderId);

		if (currentIndex > 0) {
			// Previous order is on current page
			const prevOrderId = currentOrdersList[currentIndex - 1];
			openOrderDetails(prevOrderId, detailsTab);
		} else if (currentIndex === 0 && currentPage > 1) {
			// Need to go to previous page - go to last item of previous page
			pendingNavigationRef.current = { action: 'prev', targetTab: detailsTab };
			setCurrentPage(currentPage - 1);
		}
		// If at the beginning of first page, do nothing
	};

	/**
	 * Navigate to the next order in the list
	 * Handles pagination automatically if needed
	 * @param currentOrderId - The ID of the currently displayed order
	 * @param detailsTab - The tab to maintain when navigating (defaults to PROGRESS)
	 */
	const navigateToNextOrder = (
		currentOrderId: string,
		detailsTab: OrderDetailsTab = OrderDetailsTab.PROGRESS,
	) => {
		const currentIndex = currentOrdersList.indexOf(currentOrderId);

		if (currentIndex >= 0 && currentIndex < currentOrdersList.length - 1) {
			// Next order is on current page
			const nextOrderId = currentOrdersList[currentIndex + 1];
			openOrderDetails(nextOrderId, detailsTab);
		} else if (currentIndex === currentOrdersList.length - 1) {
			// Need to go to next page - go to first item of next page
			pendingNavigationRef.current = { action: 'next', targetTab: detailsTab };
			setCurrentPage(currentPage + 1);
		}
		// If at the end of results, do nothing (boundary check should disable button)
	};

	/**
	 * Get navigation state for the current order
	 * Returns information about whether prev/next navigation is possible
	 */
	const getNavigationState = (currentOrderId: string, totalCount: number) => {
		const currentIndex = currentOrdersList.indexOf(currentOrderId);
		const isFirstOnPage = currentIndex === 0;
		const isLastOnPage = currentIndex === currentOrdersList.length - 1;
		const isFirstPage = currentPage === 1;
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const isLastPage = currentPage >= totalPages;

		return {
			canGoPrevious: !(isFirstOnPage && isFirstPage),
			canGoNext: !(isLastOnPage && isLastPage),
			currentIndex,
			totalInCurrentPage: currentOrdersList.length,
		};
	};

	// Effect to handle navigation after page change
	useEffect(() => {
		if (pendingNavigationRef.current && currentOrdersList.length > 0) {
			const { action, targetTab } = pendingNavigationRef.current;

			if (action === 'next') {
				// Navigate to first order of new page
				openOrderDetails(currentOrdersList[0], targetTab);
			} else if (action === 'prev') {
				// Navigate to last order of new page
				openOrderDetails(currentOrdersList[currentOrdersList.length - 1], targetTab);
			}

			// Clear pending navigation
			pendingNavigationRef.current = null;
		}
	}, [currentOrdersList, openOrderDetails]);

	return {
		openOrderDetails,
		closeOrderDetails,
		switchOrderDetailsTab,
		navigateToPreviousOrder,
		navigateToNextOrder,
		getNavigationState,
		// Expose params in case components need them
		servicesProviderId,
		tabId,
		orderId,
	};
}
